import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Star, TrendingUp, MapPin, Briefcase, UserPlus, Users, Award, BarChart3, Zap } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";
import { toast } from "sonner";

const CHART_COLORS = ["#074572", "#F85037", "#10B981", "#F59E0B", "#8B5CF6", "#EC4899", "#06B6D4", "#84CC16"];

function SpotlightCard({ athlete, rank }) {
  const initials = athlete.name?.split(" ").map(n => n[0]).join("").toUpperCase() || "?";
  const handleConnect = async () => {
    try {
      await api.post(`/connections/request/${athlete._id}`);
      toast.success("Connection request sent!");
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to connect"); }
  };

  return (
    <div className="scout-card bg-white border border-[#E6E6E6] rounded-lg p-6 relative overflow-hidden flex flex-col h-full" data-testid={`spotlight-card-${athlete._id}`}>
      {rank <= 3 && (
        <div className={`absolute top-0 right-0 px-3 py-1 text-[10px] font-bold text-white rounded-bl-lg ${
          rank === 1 ? "bg-[#F85037]" : rank === 2 ? "bg-[#074572]" : "bg-zinc-500"
        }`}>
          #{rank}
        </div>
      )}
      <div className="flex items-start gap-4">
        <Link to={`/profile/${athlete._id}`}>
          <Avatar className="h-16 w-16 border-2 border-[#E6E6E6]">
            <AvatarFallback className="bg-[#074572] text-white text-lg font-semibold">{initials}</AvatarFallback>
          </Avatar>
        </Link>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <Link to={`/profile/${athlete._id}`} className="font-semibold text-zinc-900 hover:text-[#074572] truncate" style={{ fontFamily: 'Outfit' }}>
              {athlete.name}
            </Link>
            <div className="flex items-center gap-1 px-2 py-0.5 bg-amber-50 rounded-full shrink-0">
              <Star size={10} className="text-amber-500 fill-amber-500" />
              <span className="text-[10px] font-bold text-amber-700">{athlete.spotlight_score}</span>
            </div>
          </div>
          <div className="flex items-center gap-2 mt-1 min-h-[20px]">
            {athlete.position && <Badge variant="secondary" className="text-[10px] bg-[#074572]/5 text-[#074572]">{athlete.position}</Badge>}
            {athlete.age && <span className="text-xs text-zinc-400">{athlete.age} yrs</span>}
          </div>
          <div className="flex items-center gap-3 mt-2 text-xs text-zinc-400 min-h-[16px]">
            {athlete.location && <span className="flex items-center gap-1"><MapPin size={11} />{athlete.location}</span>}
            {athlete.club && <span className="flex items-center gap-1"><Briefcase size={11} />{athlete.club}</span>}
          </div>
          <div className="flex flex-wrap gap-1 mt-3 min-h-[44px] content-start">
            {athlete.skills?.slice(0, 4).map(s => (
              <span key={s} className="text-[10px] px-2 py-0.5 bg-zinc-50 text-zinc-500 rounded-full border border-zinc-100 h-[18px]">{s}</span>
            ))}
          </div>
        </div>
      </div>
      <Separator className="bg-[#E6E6E6] my-4" />
      <div className="grid grid-cols-3 gap-2 mb-4 mt-auto">
        <div className="text-center p-2 bg-zinc-50 rounded-md">
          <p className="text-sm font-semibold text-[#074572]">{athlete.experience_years || 0}</p>
          <p className="text-[10px] text-zinc-400">Years Exp</p>
        </div>
        <div className="text-center p-2 bg-zinc-50 rounded-md">
          <p className="text-sm font-semibold text-[#074572]">{athlete.post_count || 0}</p>
          <p className="text-[10px] text-zinc-400">Posts</p>
        </div>
        <div className="text-center p-2 bg-zinc-50 rounded-md">
          <p className="text-sm font-semibold text-[#074572]">{athlete.connection_count || 0}</p>
          <p className="text-[10px] text-zinc-400">Connections</p>
        </div>
      </div>
      <div className="flex gap-2">
        <Link to={`/profile/${athlete._id}`} className="flex-1">
          <Button variant="outline" size="sm" className="w-full border-[#E6E6E6] text-[#074572] text-xs">View Profile</Button>
        </Link>
        <Button size="sm" className="bg-[#F85037] hover:bg-[#e04530] text-white text-xs gap-1" onClick={handleConnect} data-testid={`spotlight-connect-${athlete._id}`}>
          <UserPlus size={12} /> Connect
        </Button>
      </div>
    </div>
  );
}

function RecommendationCard({ user: rec }) {
  const initials = rec.name?.split(" ").map(n => n[0]).join("").toUpperCase() || "?";
  const handleConnect = async () => {
    try {
      await api.post(`/connections/request/${rec._id}`);
      toast.success("Connection request sent!");
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="flex items-center gap-3 p-3 bg-white border border-[#E6E6E6] rounded-md hover:shadow-sm transition-shadow" data-testid={`rec-card-${rec._id}`}>
      <Link to={`/profile/${rec._id}`}>
        <Avatar className="h-10 w-10 border border-[#E6E6E6]">
          <AvatarFallback className="bg-[#074572] text-white text-xs">{initials}</AvatarFallback>
        </Avatar>
      </Link>
      <div className="flex-1 min-w-0">
        <Link to={`/profile/${rec._id}`} className="text-sm font-medium text-zinc-900 hover:text-[#074572] truncate block">{rec.name}</Link>
        <div className="flex items-center gap-2">
          <span className="text-xs text-zinc-400 capitalize">{rec.role}</span>
          {rec.position && <span className="text-xs text-zinc-400">- {rec.position}</span>}
        </div>
      </div>
      {rec.match_score > 0 && (
        <span className="text-[10px] font-semibold text-[#074572] bg-[#074572]/5 px-2 py-0.5 rounded-full">{rec.match_score}% match</span>
      )}
      <Button size="sm" variant="ghost" className="text-[#F85037] hover:bg-[#F85037]/5" onClick={handleConnect}>
        <UserPlus size={14} />
      </Button>
    </div>
  );
}

export default function TalentSpotlightPage() {
  const { user } = useAuth();
  const isAdmin = ["admin", "super_admin", "moderator", "verification_admin"].includes(user?.role);
  const [spotlight, setSpotlight] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [recommendations, setRecommendations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const requests = [
          api.get("/scout/spotlight"),
          api.get("/scout/recommendations"),
        ];
        if (isAdmin) requests.push(api.get("/scout/analytics"));
        const [spotRes, recRes, analyticsRes] = await Promise.all(requests);
        setSpotlight(spotRes.data);
        setRecommendations(recRes.data);
        if (isAdmin && analyticsRes) setAnalytics(analyticsRes.data);
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    };
    load();
  }, [isAdmin]);

  if (loading) return <div className="flex items-center justify-center h-96 text-zinc-400"><div className="w-8 h-8 border-2 border-[#074572] border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6" data-testid="talent-spotlight-page">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-lg bg-[#F85037]/10 flex items-center justify-center">
            <Zap size={20} className="text-[#F85037]" />
          </div>
          <div>
            <h1 className="text-2xl font-medium text-zinc-900" style={{ fontFamily: 'Outfit' }}>Talent Spotlight</h1>
            <p className="text-sm text-zinc-500">This week's rising athletes and network insights</p>
          </div>
        </div>
      </div>

      <Tabs defaultValue="spotlight">
        <TabsList className="bg-transparent border-b border-[#E6E6E6] rounded-none w-full justify-start gap-6 px-0 h-auto pb-0">
          <TabsTrigger value="spotlight" className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#074572] data-[state=active]:text-[#074572] data-[state=active]:shadow-none bg-transparent px-1 pb-3 text-sm gap-2" data-testid="tab-spotlight">
            <Star size={14} /> Spotlight
          </TabsTrigger>
          {isAdmin && (
            <TabsTrigger value="analytics" className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#074572] data-[state=active]:text-[#074572] data-[state=active]:shadow-none bg-transparent px-1 pb-3 text-sm gap-2" data-testid="tab-analytics">
              <BarChart3 size={14} /> Analytics
            </TabsTrigger>
          )}
          <TabsTrigger value="recommendations" className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#074572] data-[state=active]:text-[#074572] data-[state=active]:shadow-none bg-transparent px-1 pb-3 text-sm gap-2" data-testid="tab-recommendations">
            <TrendingUp size={14} /> For You
          </TabsTrigger>
        </TabsList>

        {/* Spotlight Tab */}
        <TabsContent value="spotlight" className="mt-6">
          <div className="flex items-center gap-2 mb-4">
            <Award size={16} className="text-amber-500" />
            <h2 className="text-sm font-semibold text-zinc-900 uppercase tracking-wide" style={{ fontFamily: 'Outfit' }}>
              Featured Athletes This Week
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 items-stretch">
            {spotlight.map((a, idx) => <SpotlightCard key={a._id} athlete={a} rank={idx + 1} />)}
          </div>
          {spotlight.length === 0 && <p className="text-center py-12 text-zinc-400">No spotlight athletes yet</p>}
        </TabsContent>

        {/* Analytics Tab (admin only) */}
        {isAdmin && (
        <TabsContent value="analytics" className="mt-6">
          {analytics && (
            <>
              {/* Summary Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-8">
                {[
                  { label: "Total Users", value: analytics.totals.users, icon: Users, color: "#074572" },
                  { label: "Athletes", value: analytics.totals.athletes, icon: Zap, color: "#F85037" },
                  { label: "Scouts", value: analytics.totals.scouts, icon: Star, color: "#10B981" },
                  { label: "Coaches", value: analytics.totals.coaches, icon: Award, color: "#F59E0B" },
                  { label: "Posts", value: analytics.totals.posts, icon: TrendingUp, color: "#8B5CF6" },
                  { label: "Events", value: analytics.totals.events, icon: BarChart3, color: "#06B6D4" },
                ].map(s => (
                  <div key={s.label} className="bg-white border border-[#E6E6E6] rounded-lg p-4" data-testid={`stat-${s.label.toLowerCase().replace(' ', '-')}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <s.icon size={14} style={{ color: s.color }} />
                      <span className="text-[10px] font-semibold text-zinc-400 uppercase tracking-wider">{s.label}</span>
                    </div>
                    <p className="text-2xl font-semibold text-zinc-900" style={{ fontFamily: 'Outfit' }}>{s.value}</p>
                  </div>
                ))}
              </div>

              {/* Charts */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Position Distribution */}
                {analytics.positions.length > 0 && (
                  <div className="bg-white border border-[#E6E6E6] rounded-lg p-6" data-testid="chart-positions">
                    <h3 className="text-sm font-semibold text-zinc-900 mb-4" style={{ fontFamily: 'Outfit' }}>Athletes by Position</h3>
                    <ResponsiveContainer width="100%" height={250}>
                      <BarChart data={analytics.positions}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#E6E6E6" />
                        <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#525252' }} />
                        <YAxis tick={{ fontSize: 11, fill: '#525252' }} />
                        <Tooltip contentStyle={{ fontSize: 12, border: '1px solid #E6E6E6', borderRadius: 6 }} />
                        <Bar dataKey="count" fill="#074572" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}

                {/* Role Distribution */}
                {analytics.roles.length > 0 && (
                  <div className="bg-white border border-[#E6E6E6] rounded-lg p-6" data-testid="chart-roles">
                    <h3 className="text-sm font-semibold text-zinc-900 mb-4" style={{ fontFamily: 'Outfit' }}>User Roles</h3>
                    <ResponsiveContainer width="100%" height={250}>
                      <PieChart>
                        <Pie data={analytics.roles} dataKey="count" nameKey="name" cx="50%" cy="50%" outerRadius={90} label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`} labelLine={false} fontSize={11}>
                          {analytics.roles.map((row, i) => <Cell key={row.name || `role-${i}`} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                        </Pie>
                        <Tooltip contentStyle={{ fontSize: 12, border: '1px solid #E6E6E6', borderRadius: 6 }} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                )}

                {/* Age Distribution */}
                {analytics.age_ranges.length > 0 && (
                  <div className="bg-white border border-[#E6E6E6] rounded-lg p-6" data-testid="chart-ages">
                    <h3 className="text-sm font-semibold text-zinc-900 mb-4" style={{ fontFamily: 'Outfit' }}>Age Distribution</h3>
                    <ResponsiveContainer width="100%" height={250}>
                      <BarChart data={analytics.age_ranges}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#E6E6E6" />
                        <XAxis dataKey="range" tick={{ fontSize: 11, fill: '#525252' }} />
                        <YAxis tick={{ fontSize: 11, fill: '#525252' }} />
                        <Tooltip contentStyle={{ fontSize: 12, border: '1px solid #E6E6E6', borderRadius: 6 }} />
                        <Bar dataKey="count" fill="#F85037" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}

                {/* Top Skills */}
                {analytics.top_skills?.length > 0 && (
                  <div className="bg-white border border-[#E6E6E6] rounded-lg p-6" data-testid="chart-skills">
                    <h3 className="text-sm font-semibold text-zinc-900 mb-4" style={{ fontFamily: 'Outfit' }}>Top Skills</h3>
                    <ResponsiveContainer width="100%" height={250}>
                      <BarChart data={analytics.top_skills} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" stroke="#E6E6E6" />
                        <XAxis type="number" tick={{ fontSize: 11, fill: '#525252' }} />
                        <YAxis dataKey="name" type="category" tick={{ fontSize: 11, fill: '#525252' }} width={100} />
                        <Tooltip contentStyle={{ fontSize: 12, border: '1px solid #E6E6E6', borderRadius: 6 }} />
                        <Bar dataKey="count" fill="#10B981" radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}

                {/* Locations */}
                {analytics.locations.length > 0 && (
                  <div className="bg-white border border-[#E6E6E6] rounded-lg p-6 lg:col-span-2" data-testid="chart-locations">
                    <h3 className="text-sm font-semibold text-zinc-900 mb-4" style={{ fontFamily: 'Outfit' }}>Top Locations</h3>
                    <ResponsiveContainer width="100%" height={250}>
                      <BarChart data={analytics.locations}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#E6E6E6" />
                        <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#525252' }} angle={-20} textAnchor="end" height={60} />
                        <YAxis tick={{ fontSize: 11, fill: '#525252' }} />
                        <Tooltip contentStyle={{ fontSize: 12, border: '1px solid #E6E6E6', borderRadius: 6 }} />
                        <Bar dataKey="count" fill="#8B5CF6" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>
            </>
          )}
        </TabsContent>
        )}

        {/* Recommendations Tab */}
        <TabsContent value="recommendations" className="mt-6">
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp size={16} className="text-[#074572]" />
            <h2 className="text-sm font-semibold text-zinc-900 uppercase tracking-wide" style={{ fontFamily: 'Outfit' }}>
              Recommended for You
            </h2>
          </div>
          {recommendations.length === 0 ? (
            <p className="text-center py-12 text-zinc-400">No recommendations yet. Build your profile to get better matches!</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {recommendations.map(r => <RecommendationCard key={r._id} user={r} />)}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
